import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { formatUtilityLabel, getExtractedTotalAmount } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import useStyles from './billDetailsStyles';
import { DASH, formatDate, formatMoney, getServiceGlCode, getServiceTypes, pickValue, SESSION_ONLY_HINT } from './billDetailsUtils';
import { normalizeAmounts } from './billEditUtils';
import { BillSummaryEditTable } from './BillSummaryEditTable';
import { ConfirmActionModal } from './ConfirmActionModal';
import { PropertyLineItems } from './PropertyLineItems';
const PUSH_CONFIRM_MESSAGE = 'This will push the verified bill and its mapped charges to Appfolio for the selected properties. ' + 'Make sure the bill details and utility mappings are correct before continuing.';

/** One GL code row per utility, and only for utilities whose extraction carried a code. */
function getGlCodeRows(data) {
  const rows = new Map();
  ((data === null || data === void 0 ? void 0 : data.properties) || []).forEach(property => {
    (property.services || []).forEach(service => {
      const code = getServiceGlCode(service);
      if (!code || !service.serviceType || rows.has(service.serviceType)) return;
      rows.set(service.serviceType, code);
    });
  });
  return [...rows.entries()].map(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      serviceType = _ref2[0],
      code = _ref2[1];
    return {
      code,
      serviceType
    };
  });
}

/**
 * Extracted bill summary and per-property line items (Figma: "Bill details" card).
 * Editing works on a draft that recalculates as it is typed in; saving hands it to `onSave`.
 */
export function BillDetailsCard(_ref3) {
  var _view$finalValidation, _view$finalValidation2;
  let customerName = _ref3.customerName,
    data = _ref3.data,
    _ref3$isAppfolio = _ref3.isAppfolio,
    isAppfolio = _ref3$isAppfolio === void 0 ? false : _ref3$isAppfolio,
    _ref3$isPushed = _ref3.isPushed,
    isPushed = _ref3$isPushed === void 0 ? false : _ref3$isPushed,
    onPush = _ref3.onPush,
    onSave = _ref3.onSave;
  const classes = useStyles();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    draft = _useState2[0],
    setDraft = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isPushConfirmOpen = _useState4[0],
    setIsPushConfirmOpen = _useState4[1];
  const isEditing = draft !== null;
  const view = isEditing ? draft : data;
  const summary = useMemo(() => {
    var _pickValue;
    const utilities = getServiceTypes(view);
    return {
      accountNumber: (_pickValue = pickValue(view, ['accountNumber', 'invoiceNumber', 'invoiceNo'])) !== null && _pickValue !== void 0 ? _pickValue : DASH,
      addresses: ((view === null || view === void 0 ? void 0 : view.properties) || []).map(property => property.address).filter(Boolean),
      billDate: formatDate(pickValue(view, ['billDate', 'postingDate'])),
      customFields: ((view === null || view === void 0 ? void 0 : view.customFields) || []).filter(field => field.label || field.value),
      dueDate: formatDate(pickValue(view, ['dueDate', 'billDueDate'])),
      glCodeRows: getGlCodeRows(view),
      provider: (view === null || view === void 0 ? void 0 : view.vendorName) || DASH,
      totalDue: formatMoney(getExtractedTotalAmount(view), view === null || view === void 0 ? void 0 : view.currency),
      utilities: utilities.length ? utilities.map(formatUtilityLabel).join(', ') : DASH
    };
  }, [view]);
  const isVerified = (view === null || view === void 0 || (_view$finalValidation = view.finalValidation) === null || _view$finalValidation === void 0 ? void 0 : _view$finalValidation.isValid) === true;
  const validationReason = (view === null || view === void 0 || (_view$finalValidation2 = view.finalValidation) === null || _view$finalValidation2 === void 0 ? void 0 : _view$finalValidation2.reason) || (isVerified ? 'Provider, utilities, and address match with existing property records.' : 'Some validation checks did not pass. Review the extracted values before continuing.');
  const properties = (view === null || view === void 0 ? void 0 : view.properties) || [];
  const handleSave = () => {
    onSave(normalizeAmounts(draft));
    setDraft(null);
  };
  const handleConfirmPush = () => {
    setIsPushConfirmOpen(false);
    onPush();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.card
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeaderTitles
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.cardTitle
  }, isEditing ? 'Edit bill details' : 'Bill details'), !isEditing && isAppfolio ? /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(isPushed ? classes.statusChipDone : '')
  }, isPushed ? 'Pushed to Appfolio' : 'Pending Appfolio push') : null), isEditing ? null : /*#__PURE__*/React.createElement("button", {
    "aria-label": "Edit bill details",
    className: classes.iconButton,
    disabled: !onSave,
    onClick: () => setDraft(data),
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "edit"
  }))), isEditing ? /*#__PURE__*/React.createElement(BillSummaryEditTable, {
    customerName: customerName,
    onChange: setDraft,
    value: draft
  }) : /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchBox, " ").concat(isVerified ? '' : classes.matchBoxWarning)
  }, isVerified ? /*#__PURE__*/React.createElement(Icon, {
    type: "check-circle"
  }) : /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchTitle, " ").concat(isVerified ? '' : classes.matchTitleWarning)
  }, isVerified ? 'Match: Verified' : 'Match: Review recommended'), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.matchText, " ").concat(isVerified ? '' : classes.matchTextWarning)
  }, validationReason))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Summary:"), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Customer:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, (view === null || view === void 0 ? void 0 : view.customerName) || customerName || DASH)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Provider:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.provider)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Utilities:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.utilities)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Properties:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.addresses.length ? summary.addresses.map(address => /*#__PURE__*/React.createElement("div", {
    key: address
  }, address)) : DASH))), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Account #:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.accountNumber)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Bill date:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.billDate)), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Due:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.dueDate))), summary.glCodeRows.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, summary.glCodeRows.map(row => /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow,
    key: row.serviceType
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelWide
  }, formatUtilityLabel(row.serviceType), " GL code:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, row.code)))) : null, summary.customFields.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, summary.customFields.map((field, index) => /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow,
    key: "".concat(field.label, "-").concat(index)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabelWide
  }, field.label || DASH, ":"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, field.value || DASH)))) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.summaryRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Total due:"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, summary.totalDue))), /*#__PURE__*/React.createElement("div", {
    className: classes.sectionLabel
  }, "Line items by property:")), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editSectionRow
  }, "Line items per property:") : null, properties.length ? properties.map((property, index) => /*#__PURE__*/React.createElement(PropertyLineItems, {
    currency: view === null || view === void 0 ? void 0 : view.currency,
    data: view,
    isEditing: isEditing,
    key: property.address || index,
    onChange: setDraft,
    propertyIndex: index
  })) : /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, "No property line items were extracted for this bill."), isEditing ? /*#__PURE__*/React.createElement("div", {
    className: classes.editFooter
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDraft(null),
    size: "small",
    variant: "outlined"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSave,
    size: "small",
    variant: "contained"
  }, "Save")) : null, !isEditing && isAppfolio ? /*#__PURE__*/React.createElement("div", {
    className: classes.cardActions
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: SESSION_ONLY_HINT,
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !onPush || isPushed,
    onClick: () => setIsPushConfirmOpen(true),
    size: "small",
    variant: "contained"
  }, isPushed ? 'Pushed to Appfolio' : 'Push to Appfolio'))), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Push to Appfolio",
    isOpen: isPushConfirmOpen,
    message: PUSH_CONFIRM_MESSAGE,
    onClose: () => setIsPushConfirmOpen(false),
    onConfirm: handleConfirmPush,
    title: "Push bill to Appfolio?"
  })) : null);
}